/**
 * Keeps parallel cables legible.
 *
 * Two devices are rarely joined by one cable. A camera chain is SDI plus
 * genlock plus comms; a switcher feeds a multiviewer over several outputs.
 * Routed naively every one of those takes the identical orthogonal path, so
 * four cables draw as one line and the diagram tells you nothing about how
 * many runs the rigger actually has to pull.
 *
 * So cables sharing a pair of devices are spread into lanes: same route, fixed
 * gap, symmetric about where the single-cable path would have been. Crossings
 * between bundles are untouched — two cables that genuinely cross still cross,
 * because that is real information. Within a bundle, the cables take the lane
 * order that crosses them least, so a pair of runs between the same two
 * devices sits side by side instead of tangling at the ends.
 *
 * Pure, so the spacing is testable without a browser.
 */
import { cableRoute, countCrossings, laneAxis, STEP_GAP, straightRuns, type StraightRuns } from './cable-route'

/** Gap between adjacent lanes, in flow units. Wide enough to read at 50% zoom. */
export const LANE_GAP = 16

/** Beyond this, lanes would sprawl further than they clarify. */
const MAX_SPREAD = 7

/**
 * Where one cable sits within its bundle, as an offset from the centre.
 *
 * Symmetric so a bundle stays centred on the route the single cable would have
 * taken: two cables sit at ±8, three at −16/0/+16. An odd bundle therefore
 * keeps one cable exactly where it was.
 */
export function laneOffset(index: number, count: number): number {
  if (count <= 1 || index < 0) return 0
  // A very large bundle stops spreading and starts stacking two-deep rather
  // than marching off across the canvas.
  const effective = Math.min(count, MAX_SPREAD)
  const position = index % effective
  const depth = Math.floor(index / effective)
  return (position - (effective - 1) / 2) * LANE_GAP + depth * (LANE_GAP / 3)
}

/** Cables between the same two devices, whichever way round they were drawn. */
export function pairKey(source: string, target: string): string {
  return source < target ? `${source} ${target}` : `${target} ${source}`
}

export type LaneAssignment = { offset: number; index: number; count: number }

/**
 * Where a cable's middle segment would fall if it were drawn alone.
 *
 * A smooth-step route between a right-hand output and a left-hand input has
 * one long straight in the middle — the trunk. `axis` says which way that
 * trunk runs ('x' means it is a vertical line at x = `trunk`, separated
 * sideways; 'y' means a horizontal line at y = `trunk`), and `lo`/`hi` are
 * its extent along the other direction. Two cables whose trunks sit within a
 * lane of each other and whose extents overlap would draw over one another.
 */
export type EdgeGeometry = {
  axis: 'x' | 'y'
  trunk: number
  lo: number
  hi: number
  /** The cable's two ends, when known; the lane order is worked out from them. */
  ends?: CableEnds
}

/** Where a cable leaves its output and where it meets its input. */
export type CableEnds = { sourceX: number; sourceY: number; targetX: number; targetY: number }

/**
 * Assigns every edge its lane.
 *
 * Cables are grouped by the trunk they would share: when geometry is known,
 * that is anything within a lane of the same line whose extents overlap, so
 * two cables from different devices into the same input column are separated
 * just like two between the same pair. Without geometry (nodes not yet
 * measured) it falls back to grouping by device pair.
 *
 * Within a group, cables take the lane order that crosses them least (see
 * `leastCrossingOrder`); ties, and groups with no geometry, fall back to edge
 * id rather than array position, so a lane does not jump when an unrelated
 * cable is added or deleted.
 */
export function assignLanes<E extends { id: string; source: string; target: string }>(
  edges: E[],
  geometryOf?: (edge: E) => EdgeGeometry | undefined,
): Map<string, LaneAssignment> {
  const groups: string[][] = []
  const byPair = new Map<string, string[]>()
  const placed: Array<{ id: string; geometry: EdgeGeometry }> = []
  const endsById = new Map<string, CableEnds>()

  for (const edge of edges) {
    const geometry = geometryOf?.(edge)
    if (geometry) {
      placed.push({ id: edge.id, geometry })
      if (geometry.ends) endsById.set(edge.id, geometry.ends)
      continue
    }
    const key = pairKey(edge.source, edge.target)
    const bucket = byPair.get(key)
    if (bucket) bucket.push(edge.id)
    else byPair.set(key, [edge.id])
  }
  groups.push(...byPair.values())

  // Cables that would share a trunk are clustered per axis. Sorting by trunk
  // position means a cluster can be grown in one pass: a cable joins the open
  // cluster when it sits within a lane of the last trunk and its extent
  // touches the cluster's. Overlap is transitive on purpose — if A collides
  // with B and B with C, all three must be laid out together.
  for (const axis of ['x', 'y'] as const) {
    const sorted = placed
      .filter((p) => p.geometry.axis === axis)
      .sort((a, b) => a.geometry.trunk - b.geometry.trunk || (a.id < b.id ? -1 : 1))
    let open: { ids: string[]; trunk: number; lo: number; hi: number } | null = null
    for (const { id, geometry } of sorted) {
      const near = open !== null && geometry.trunk - open.trunk < LANE_GAP
      const touches = open !== null && geometry.lo <= open.hi && geometry.hi >= open.lo
      if (open && near && touches) {
        open.ids.push(id)
        open.trunk = geometry.trunk
        open.lo = Math.min(open.lo, geometry.lo)
        open.hi = Math.max(open.hi, geometry.hi)
      } else {
        if (open) groups.push(open.ids)
        open = { ids: [id], trunk: geometry.trunk, lo: geometry.lo, hi: geometry.hi }
      }
    }
    if (open) groups.push(open.ids)
  }

  const lanes = new Map<string, LaneAssignment>()
  for (const ids of groups) {
    const ordered = leastCrossingOrder([...ids].sort(), endsById)
    ordered.forEach((id, index) => {
      lanes.set(id, { offset: laneOffset(index, ordered.length), index, count: ordered.length })
    })
  }
  return lanes
}

/**
 * The trunk a cable would take between two handle points, for `assignLanes`.
 */
export function edgeGeometry(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): EdgeGeometry {
  const axis = laneAxis(sourceX, sourceY, targetX, targetY)
  const ends = { sourceX, sourceY, targetX, targetY }
  return axis === 'x'
    ? {
        axis,
        trunk: (sourceX + targetX) / 2,
        lo: Math.min(sourceY, targetY),
        hi: Math.max(sourceY, targetY),
        ends,
      }
    : {
        axis,
        trunk: (sourceY + targetY) / 2,
        lo: Math.min(sourceX, targetX) - STEP_GAP,
        hi: Math.max(sourceX, targetX) + STEP_GAP,
        ends,
      }
}

/**
 * The lane order for one bundle that crosses its cables least.
 *
 * Whether two cables in a bundle cross depends only on which of them takes
 * the earlier lane (left of a vertical trunk, above a horizontal one): two
 * runs dropping from one device to the next cross twice when the upper one
 * takes the inner lane, and not at all the other way round. So each pair is
 * priced both ways, from the routes the canvas would actually draw, and the
 * order is improved one move at a time — take a cable out, put it back
 * wherever the total is lowest — until no move helps. Bundles are a handful
 * of cables, so this is a few hundred lookups. A move must strictly lower the
 * total, so ties keep `ids` order (edge id) and the layout stays put.
 */
function leastCrossingOrder(ids: string[], endsById: ReadonlyMap<string, CableEnds>): string[] {
  if (ids.length < 2) return ids
  const runs: Array<{ before: StraightRuns; after: StraightRuns }> = []
  for (const id of ids) {
    const ends = endsById.get(id)
    if (!ends) return ids
    const at = (offset: number) =>
      straightRuns(cableRoute(ends.sourceX, ends.sourceY, ends.targetX, ends.targetY, offset).points)
    runs.push({ before: at(-LANE_GAP / 2), after: at(LANE_GAP / 2) })
  }

  // cost[i][j]: crossings between i and j when i takes the earlier lane.
  const n = ids.length
  const cost = runs.map((a, i) => runs.map((b, j) => (i === j ? 0 : countCrossings(a.before, b.after))))
  const price = (i: number, j: number) => cost[i]?.[j] ?? 0

  let order = ids.map((_, i) => i)
  for (let moves = 0; moves < n * n; moves++) {
    let moved = false
    for (let from = 0; from < n && !moved; from++) {
      const cable = order[from]
      if (cable === undefined) continue
      const rest = order.filter((_, k) => k !== from)
      // Placing the cable at `to`: everything before it pays price(other, cable),
      // everything after pays price(cable, other).
      const at = (to: number) =>
        rest.reduce((sum, other, k) => sum + (k < to ? price(other, cable) : price(cable, other)), 0)
      const here = at(from)
      let best = from
      let bestCost = here
      for (let to = 0; to <= rest.length; to++) {
        const c = at(to)
        if (c < bestCost) {
          best = to
          bestCost = c
        }
      }
      if (best !== from) {
        order = [...rest.slice(0, best), cable, ...rest.slice(best)]
        moved = true
      }
    }
    if (!moved) break
  }
  return order.map((i) => ids[i] ?? '')
}
